import { useRef, useState } from "react";
import { Alert, Card, Col, Flex, InputRef, Row, Select, Typography } from "antd";
import {
  DollarOutlined,
  TeamOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import MetricCard from "components/MetricCard.component";
import { getReferralAnalytics } from "hooks/queries/analytics";
import { useTableSort } from "hooks/useTableSort";
import { translate } from "shared/languages";
import {
  formatCurrency,
  INACTIVE_REASON_LABELS,
  INACTIVE_REASON_TONE,
  UPGRADE_LABEL,
} from "shared/utils";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { colors } from "shared/theme/color";
import { friendDiscountElsewhere, rewardLabel } from "shared/utils/loyalty";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import {
  AnalyticsRange,
  CustomDayRange,
  InactiveReason,
  RetentionBound,
  RetentionUnit,
  ReferralConversionRow,
} from "shared/types";
import PATHS from "shared/constants/paths";
import { tzDate, getYtdDayRange } from "shared/utils/dayjs";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { useAuth } from "context/AuthContextProvider";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import InlineDaysInput from "./InlineDaysInput";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import ReferralTrendChart from "./ReferralTrendChart";
import RetentionMeter from "./RetentionMeter";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { byNumber } from "shared/reports/columnCatalogue";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { referralSpec } from "pdf/reports/specs/reward";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

// Matches LoyaltyPerformancePage: 1.4x the shared viewBox height. The chart
// sizes its own viewBox width to its container, so it fills the half-column
// it now shares with the retention meter.
const { Text } = Typography;

const CHART_HEIGHT = 240;

// Mirrors the server's clamps, expressed in days so the two ends stay
// comparable across units. The per-unit day counts are for clamping and
// ordering only; the server does real calendar arithmetic.
const DAYS_PER_UNIT: Record<RetentionUnit, number> = {
  day: 1,
  month: 30,
  year: 365,
  now: 0,
};
const FROM_DAYS = { min: 60, max: 1095 };
// Floor of 1, not 7: "sekarang" already offers a zero-day wait.
const TO_DAYS = { min: 1, max: 365 };
const SPAN_UNITS: RetentionUnit[] = ["day", "month", "year"];
// Only the tail offers "sekarang" (no maturity wait). A floor of "now" would
// be an empty window.
const TAIL_UNITS: RetentionUnit[] = [...SPAN_UNITS, "now"];
const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontWeight: 600,
  fontSize: textPx(16),
  color: colors.ink,
};
const sectionDescStyle: React.CSSProperties = {
  fontSize: textPx(12),
  color: colors.ink3,
  marginTop: 4,
  marginBottom: 18,
};

const RETENTION_FROM_DEFAULT: RetentionBound = { value: 12, unit: "month" };
const RETENTION_TO_DEFAULT: RetentionBound = { value: 30, unit: "day" };

const approxDays = (b: RetentionBound) =>
  b.unit === "now" ? 0 : b.value * DAYS_PER_UNIT[b.unit];
const unitLabel = (u: RetentionUnit) =>
  translate(`analyticsPage.referral.unit_${u}`);

// The number field's own min/max depend on the chosen unit, so the day-based
// clamp is projected onto it. Floors at 1 so a unit is never unusable.
const limitsFor = (unit: RetentionUnit, days: { min: number; max: number }) => ({
  min: Math.max(1, Math.ceil(days.min / DAYS_PER_UNIT[unit])),
  max: Math.max(1, Math.floor(days.max / DAYS_PER_UNIT[unit])),
});

// Switching unit CONVERTS rather than reinterpreting: 12 bulan becomes 1
// tahun, not 12 tahun. Reinterpreting would silently multiply the window by
// twelve on a single click.
const convertBound = (
  b: RetentionBound,
  unit: RetentionUnit,
  days: { min: number; max: number },
): RetentionBound => {
  // "now" carries no number, and leaving it restores the default tail rather
  // than reinterpreting a zero.
  if (unit === "now") return { value: 0, unit };
  if (b.unit === "now") return convertBound(RETENTION_TO_DEFAULT, unit, days);
  const lim = limitsFor(unit, days);
  const converted = Math.round(approxDays(b) / DAYS_PER_UNIT[unit]);
  return { value: Math.min(lim.max, Math.max(lim.min, converted || 1)), unit };
};

// A number field plus its unit, sitting inline in the sentence.
const BoundInput = ({
  value,
  onChange,
  days,
  units,
}: {
  value: RetentionBound;
  onChange: (next: RetentionBound) => void;
  days: { min: number; max: number };
  units: RetentionUnit[];
}) => {
  const lim = limitsFor(value.unit, days);
  return (
    <Flex align="center" gap={6}>
      {/* "sekarang" is a point in time, not a span, so it has no number. */}
      {value.unit !== "now" && (
        <InlineDaysInput
          value={value.value}
          onChange={(n) => onChange({ value: n, unit: value.unit })}
          min={lim.min}
          max={lim.max}
        />
      )}
      <Select
        size="middle"
        value={value.unit}
        onChange={(unit: RetentionUnit) => onChange(convertBound(value, unit, days))}
        options={units.map((u) => ({ value: u, label: unitLabel(u) }))}
        style={{ width: 106 }}
      />
    </Flex>
  );
};

const ReferralPerformancePage = () => {
  const { state: authState } = useAuth();
  const navigate = useNavigate();
  const { sortState, onSortChange } = useTableSort();
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const [retentionFrom, setRetentionFrom] = useState<RetentionBound>(
    RETENTION_FROM_DEFAULT,
  );
  const [retentionTo, setRetentionTo] = useState<RetentionBound>(RETENTION_TO_DEFAULT);
  const isYtdActive =
    customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getReferralAnalytics(
    range,
    customRange,
    retentionFrom,
    retentionTo,
  );

  const windowParams = {
    ...(customRange
      ? { from_date: customRange.from, to_date: customRange.to }
      : { range }),
    retention_from_value: retentionFrom.value,
    retention_from_unit: retentionFrom.unit,
    retention_to_value: retentionTo.value,
    retention_to_unit: retentionTo.unit,
  };

  const columns: ReportColumn<ReferralConversionRow>[] = [
    reportColumn<ReferralConversionRow>("converted_at", {
      dataIndex: "converted_at",
      sorter: (a, b) => tzDate(a.converted_at).valueOf() - tzDate(b.converted_at).valueOf(),
      defaultSortOrder: "descend",
    }),
    reportColumn<ReferralConversionRow>("referrer", {
      dataIndex: "referrer_name",
      search: getColumnSearchProps<ReferralConversionRow, "referrer_name">({
        config: "referrer_name",
        searchInput,
      }),
    }),
    // The new client is the row's subject (bold, stretching column); the
    // referrer beside it is a plain text column with the search.
    reportColumn<ReferralConversionRow>("new_client", { dataIndex: "referred_name" }),
    reportColumn<ReferralConversionRow>("referrer_reward", {
      key: "referrer_reward",
      // Null when the programme had no referrer-side reward configured at
      // conversion time — a real state, not a missing join.
      render: (_: unknown, row) =>
        row.referrer_reward_type
          ? rewardLabel({
              type: row.referrer_reward_type,
              credit_qty: row.referrer_credit_qty,
              discount_mode: row.referrer_discount_mode,
              discount_value: row.referrer_discount_value,
            })
          : "-",
    }),
    reportColumn<ReferralConversionRow>("new_client_reward", {
      // The friend's side of the deal, described the same way as the
      // referrer's. Both sides can be a free credit, so a rupiah-only column
      // could never show one. Replaces "Diskon Teman", which stated the same
      // reward a second time in rupiah and was blank whenever it was a
      // credit; the realized rupiah of each use is in the Pemakaian Reward
      // report.
      key: "friend_reward",
      render: (_: unknown, row) => {
        if (!row.friend_reward_type) return "-";
        const reward = rewardLabel({
          type: row.friend_reward_type,
          credit_qty: row.friend_credit_qty,
          discount_mode: row.friend_discount_mode,
          discount_value: row.friend_discount_value,
        });
        const elsewhere = friendDiscountElsewhere(row);
        // The discount went on a later sale, not the one in Produk Konversi:
        // name it, or the full price beside it reads as the reward missing
        // (System Review R20).
        return elsewhere ? (
          <Flex vertical>
            <span>{reward}</span>
            <Text type="secondary" style={{ fontSize: textPx(12) }}>
              {elsewhere}
            </Text>
          </Flex>
        ) : (
          reward
        );
      },
    }),
    reportColumn<ReferralConversionRow>("conversion_product", {
      dataIndex: "package_name",
      // The same cell as Pembelian's product: the name with at most ONE chip
      // under it. "Voided" is a STATUS (what happened to the sale, with its
      // dot) and wins when both apply; otherwise "Upgrade", the LABEL that
      // explains a fee-sized value. Nothing is struck (chris, 2026-10-17).
      // The row itself is not dimmed: its subject is the referral, which
      // stays converted when the sale is voided after a reward was used.
      render: (value: string | null, row) => (
        <Flex vertical align="flex-start" gap={4}>
          <span>{value ?? "-"}</span>
          {row.is_voided === true ? (
            <StatusBadge
              tone={INACTIVE_REASON_TONE[InactiveReason.MANUALLY_MARKED_INVALID]}
              label={INACTIVE_REASON_LABELS[InactiveReason.MANUALLY_MARKED_INVALID]}
            />
          ) : row.is_upgrade === true ? (
            <StatusBadge tone="navy" label={UPGRADE_LABEL} dot={false} />
          ) : null}
        </Flex>
      ),
    }),
    // Pembatasan Admin: what each conversion brought in is revenue, the same
    // cashCollected figure the hidden Pembelian reports carry. The reward
    // columns above stay: what the programme cost is not revenue.
    ...(data?.money_hidden
      ? []
      : [
          reportColumn<ReferralConversionRow>("purchase_value", {
            dataIndex: "first_purchase_value",
            sorter: byNumber<ReferralConversionRow>("first_purchase_value"),
            // A voided converting sale brought in nothing: a dash, never
            // "Rp 0", which would read as a free package.
            render: (value: number | null, row) =>
              row.is_voided ? "-" : value === null ? "-" : formatCurrency(value),
          }),
        ]),
  ];

  const costPerClient = data?.discount_cost_per_client.value;
  const ret = data?.retention;

  const pageCount = useReportPageCount(referralSpec, data, sortState);

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.referral.report_performance")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="performa-referral"
            params={windowParams}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table.length, MAX_PDF_ROWS),
                  total: data.table_total,
                },
                fileName: `spott-performa-referral-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "performa-referral",
                    studio: authState.business?.name ?? "",
                    data,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        {data?.program_status === "off" && (
          <Alert
            type="info"
            showIcon
            message={translate("analyticsPage.referral.program_off")}
          />
        )}
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>{data?.range_label}</span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker
              value={isYtdActive ? null : customRange}
              onChange={setCustomRange}
            />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>

        {/* Reach, benefit and cost of the programme. Discount totals, sales
            with a referral discount and credits granted were dropped on
            2026-09-24 (Andrew): each is visible elsewhere (Pembelian's Diskon
            filter, the conversions table's reward columns, and Pemakaian
            Reward for what the credits were used on). */}
        <Flex gap={16} wrap="wrap">
          {/* Registrations of any status, named as such: "Klien Baru" on this
              page means a converted client everywhere else (the table, the
              trend, the cost figure's denominator). */}
          <MetricCard
            icon={<UserAddOutlined />}
            title={translate("analyticsPage.referral.kpi_new_clients")}
            information={translate("analyticsPage.referral.kpi_new_clients_info")}
            value={data?.new_clients.value ?? 0}
          />
          {/* Dropped with the Nilai Pembelian column, never alone: masking only the card
              would leave the per-row figures to be summed. Biaya diskon per
              klien baru beside it stays, which is the cost side. */}
          {!data?.money_hidden && (
            <MetricCard
              icon={<DollarOutlined />}
              title={translate("analyticsPage.referral.kpi_first_purchase_revenue")}
              information={translate("analyticsPage.referral.kpi_first_purchase_revenue_info")}
              value={formatCurrency(data?.first_purchase_revenue?.value ?? 0)}
            />
          )}
          <MetricCard
            icon={<TeamOutlined />}
            title={translate("analyticsPage.referral.kpi_cost_per_client")}
            information={translate("analyticsPage.referral.kpi_cost_per_client_info")}
            // Null (no conversions) is a dash, never "Rp 0" — zero clients at
            // zero cost is not a cost of zero.
            value={costPerClient === null || costPerClient === undefined
              ? "-"
              : formatCurrency(costPerClient)}
          />
        </Flex>

        {/* Trend and retention side by side: both describe how the programme
            is doing over time, and the meter is a single figure that never
            needed a full-width card of its own. The meter answers over its
            OWN window, which the sentence under it states and sets. */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card
              styles={{ body: { padding: 22 } }}
              loading={isLoading}
              style={{ height: "100%" }}
            >
              <div style={sectionHeadingStyle}>
                {translate("analyticsPage.referral.trend_heading")}
              </div>
              <div style={sectionDescStyle}>
                {translate("analyticsPage.referral.trend_desc")}
              </div>
              <ReferralTrendChart
                points={data?.trend.points ?? []}
                granularity={data?.trend.granularity ?? "month"}
                height={CHART_HEIGHT}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              styles={{ body: { padding: 22 } }}
              loading={isLoading}
              style={{ height: "100%" }}
            >
              <div style={sectionHeadingStyle}>
                {translate("analyticsPage.referral.retention_heading")}
              </div>
              <div style={sectionDescStyle}>
                {translate("analyticsPage.referral.retention_desc")}
              </div>
              <RetentionMeter
                matured={ret?.matured ?? 0}
                repeat={ret?.repeat ?? 0}
                notYetMatured={ret?.not_yet_matured ?? 0}
              />
              {/* The window as a sentence the admin edits in place, replacing
                  the separate "Masa Matang" card. Both ends are set here, so
                  the rule and the figure it produces never sit apart. */}
              <Flex
                align="center"
                gap={8}
                wrap="wrap"
                style={{ marginTop: 18, fontSize: textPx(13), color: colors.ink2 }}
              >
                <span>{translate("analyticsPage.referral.retention_rule_prefix")}</span>
                <BoundInput
                  value={retentionFrom}
                  days={FROM_DAYS}
                  units={SPAN_UNITS}
                  onChange={(next) => {
                    setRetentionFrom(next);
                    // Keep the pair ordered when the floor moves inside the
                    // tail; the server would otherwise revert both to
                    // defaults and the sentence would jump.
                    if (approxDays(retentionTo) >= approxDays(next)) {
                      setRetentionTo(
                        convertBound(
                          { value: Math.max(1, next.value - 1), unit: next.unit },
                          retentionTo.unit,
                          TO_DAYS,
                        ),
                      );
                    }
                  }}
                />
                {/* "lalu" belongs to each bound, not to the end of the
                    sentence: the tail can be "sekarang", which is a point in
                    time and reads wrong with it. */}
                <span>{translate("analyticsPage.referral.retention_rule_suffix")}</span>
                <span>{translate("analyticsPage.referral.retention_rule_middle")}</span>
                <BoundInput
                  value={retentionTo}
                  days={TO_DAYS}
                  units={TAIL_UNITS}
                  onChange={(next) => {
                    // Same guard from the other end: a tail that reaches the
                    // floor is not a window.
                    if (approxDays(next) >= approxDays(retentionFrom)) return;
                    setRetentionTo(next);
                  }}
                />
                {retentionTo.unit !== "now" && (
                  <span>{translate("analyticsPage.referral.retention_rule_suffix")}</span>
                )}
              </Flex>
            </Card>
          </Col>
        </Row>

        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<ReferralConversionRow>
            serverTotal={{ total: data?.table_total, noun: translate("pdf.noun.konversi") }}
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            onRow={(row) => ({
              onClick: () =>
                navigate(
                  `${PATHS.CUSTOMER}/${row.referred_customer_id}?tab=5&reward=referral`,
                ),
              style: { cursor: "pointer" },
            })}
            pageMemory="analyticsReferralPerformance"
            onSortChange={onSortChange}
          />
        </Card>

      </Flex>
    </>
  );
};

export default ReferralPerformancePage;
